import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { hasPermission } from '@pec/aion-ui-core/actions/userInfo';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { IServerTokens } from '@pec/aion-ui-core/interfaces/serverTokens';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { fetchServerTokens, fetchUserInfo } from '../helpers/apiCalls';

export const AdminGuard: React.FC = ({ children }) => {
  const { i18n } = useTranslation();
  const { search } = useLocation();
  const [hasAdminPermission, setHasAdminPermission] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const isAdmin = new URLSearchParams(search).get('admin') === 'true';

  const fetchServerTokensData = async () => {
    try {
      const response = await fetchServerTokens();
      return response.data;
    } catch (error) {
      setHasError(!!error);
      throw error;
    }
  };

  const fetchUserInfoData = async (serverTokens: IServerTokens) => {
    try {
      const response = await fetchUserInfo(serverTokens);
      return response.data;
    } catch (error) {
      setHasError(!!error);
      throw error;
    }
  };

  const checkAdminPermission = React.useCallback(async () => {
    const { serverTokens, redirectUri } = await fetchServerTokensData();
    if (serverTokens) {
      const userInfo = await fetchUserInfoData(serverTokens);
      setHasAdminPermission(hasPermission(userInfo, ActivityAction.Write, ActivityResourceName.AdminSignUp));
    } else if (redirectUri) {
      window.location.assign(redirectUri);
    } else {
      setHasAdminPermission(false);
    }
  }, []);

  React.useEffect(() => {
    i18n.on('loaded', (loaded: Record<string, string[]>) => {
      // Wait for translation.json and signup.json
      if (Object.values(loaded).some(list => list.length >= 2)) {
        if (isAdmin) {
          setLoading(true);
          try {
            checkAdminPermission();
          } catch (error) {
            setHasError(!!error);
            throw error;
          } finally {
            setLoading(false);
          }
        }
      }
    });
  }, [checkAdminPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isAdmin && !hasError && (loading || hasAdminPermission === null)) {
    return <Loading />;
  }

  if (isAdmin && (hasError || hasAdminPermission === false)) {
    window.location.assign('/links');
    return null;
  }

  return <>{children}</>;
};
